import { motion } from 'framer-motion';
import { CountUp } from '@/components/roi/CountUp';
import { ContentiousModule } from '@/components/roi/ContentiousModule';
import { TierCard } from '@/components/roi/TierCard';
import {
  LARGE,
  MID,
  SMALL,
  TIERS,
  tierTotals,
  type Tier,
} from '@/lib/roi/data';
import { Disclosure } from './Disclosure';
import { ScaleBar } from './ScaleBar';
import { SectionShell } from './SectionShell';

/**
 * Per-firm ROI summary.
 *
 * Three headline figures pulled from the per-firm interviews on /roi —
 * with a single deep-link out to the full model. The pitch surface
 * never duplicates /roi's numbers in a way that could drift.
 */

function summaryFor(tier: Tier, cases: string) {
  return {
    id: tier.id,
    label: tier.label,
    valueM: tierTotals(tier).savingMid / 1_000_000,
    cases,
  };
}

const TIER_SUMMARIES = [
  summaryFor(SMALL, '100 cases/yr'),
  summaryFor(MID, '200 cases/yr'),
  summaryFor(LARGE, '100+ cases/yr (fewer, higher value)'),
];

/** Every tier bar is drawn against the largest annual saving on show. */
const TIER_SCALE_M = Math.max(...TIER_SUMMARIES.map((t) => t.valueM));

export function RoiSummary({
  linkToCalculator = false,
}: {
  /** The accepted V1 deck exits to /roi; historical compositions stay intact. */
  linkToCalculator?: boolean;
} = {}) {
  return (
    <SectionShell
      id="roi"
      index={4}
      // Left-aligned like every other section. The extra vertical air marks
      // this as the turn in the argument without breaking the deck's rhythm.
      density="open"
      eyebrow="ROI for customers"
      title={
        <>
          A <span className="text-brand-bright">six to seven figure</span>{' '}
          saving, per firm, per year
        </>
      }
      subtitle="Saving 60% of WIP costs on every case"
      intro="Time saved is a percentage of WIP across every case the firm runs. Headline figures by firm tier. Open the full model below for charge-out rates, methodology and the contentious-recovery view"
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {TIER_SUMMARIES.map((tier, i) => (
          <TierSummaryCard key={tier.id} tier={tier} index={i} />
        ))}
      </div>

      {linkToCalculator ? (
        <div className="mt-8 flex justify-center">
          <a
            href="/roi#roi"
            className="inline-flex items-center gap-3 rounded-full border border-accent-blue-border bg-accent-blue-bg px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.2em] text-brand-bright transition-colors hover:bg-bg-hover focus:outline-none focus:ring-2 focus:ring-brand-500/40"
          >
            See the full per-firm model
          </a>
        </div>
      ) : (
        <Disclosure
          label="See the full per-firm model"
          expandedLabel="Hide the full model"
        >
          <div className="flex flex-col gap-10">
            <div className="grid gap-6 lg:grid-cols-3 lg:items-start">
              {TIERS.map((tier, i) => (
                <TierCard key={tier.id} tier={tier} index={i} />
              ))}
            </div>
            <ContentiousModule />
            <p className="text-center text-[12px] text-ink-dim">
              Charge-out rates and per-case spend reflect 2025–2026 UK rates,
              sourced from discussions with around 10 firms spanning small,
              mid-tier and large segments. Figures are illustrative
            </p>
          </div>
        </Disclosure>
      )}
    </SectionShell>
  );
}

function TierSummaryCard({
  tier,
  index,
}: {
  tier: (typeof TIER_SUMMARIES)[number];
  index: number;
}) {
  const valueM = tier.valueM;
  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      whileHover={{ y: -3 }}
      transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1],
        delay: index * 0.08,
      }}
      className="pitch-card pitch-card-interactive relative isolate flex flex-col gap-4 overflow-hidden rounded-2xl p-7 sm:p-8"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-20 -right-20 -z-10 h-40 w-40 rounded-full opacity-[0.10] blur-2xl"
        style={{ background: '#3B82F6' }}
      />
      <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-ink-dim">
        {tier.label}
      </span>
      <span className="font-semibold tabular-nums text-brand-bright [font-size:52px] [line-height:0.95] sm:[font-size:60px]">
        <CountUp
          target={valueM}
          duration={1.4}
          delay={0.3}
          formatter={(n) =>
            valueM < 1
              ? `£${Math.round(n * 1000)}k`
              : `£${Math.round(n * 10) / 10}m`
          }
        />
      </span>
      {/* £180k and £4.5m are set in the same type at the same weight, so
          a twenty-five-fold difference reads as no difference at all.
          Drawn against the largest tier, the spread becomes the point. */}
      <ScaleBar to={valueM / TIER_SCALE_M} delay={0.4 + index * 0.08} />

      <span className="text-[13px] text-ink-muted">
        Annual saving · {tier.cases}
      </span>
    </motion.article>
  );
}
